import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogoutFunction } from '@propelauth/react';
import {
  Box,
  ClipboardPen,
  Download,
  FileCode,
  ExternalLink,
  House,
  Info,
  Library,
  ListTodo,
  Network,
  PanelsLeftBottom,
} from 'lucide-react';
import { I18n } from '../../i18n/I18n';
import { useGlobalStore } from '../store/GlobalStore';
import { FusawarePermission } from '../fusawareUser';
import { useDesktopDownload } from '../hooks/useDesktopDownload';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenuItem } from './ui/dropdown-menu';
import { SidebarUserMenu } from './SidebarUserMenu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from './ui/sidebar';

type NavItem = {
  key: string;
  label: string;
  icon: typeof House;
  to: string;
  /** Also active on routes under `to`, for sections with sub-pages. */
  matchNested?: boolean;
  /** Omitted for items every signed-in user can see. */
  permission?: FusawarePermission;
};

const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'dashboard', icon: House, to: '/' },
  { key: 'organizations', label: 'organizations', icon: Network, to: '/fusa-organizations' },
  { key: 'workspaces', label: 'workspaces', icon: PanelsLeftBottom, to: '/workspaces' },
  { key: 'projects', label: 'projectsUppercase', icon: Box, to: '/projects' },
  { key: 'library', label: 'library', icon: Library, to: '/library', matchNested: true },
  {
    // temporary: reports filled in from input sheets, beside the reports above until one replaces the other.
    // Fusaware staff only for now, like the report studio, until it is ready for OEMs and suppliers.
    key: 'reportsV2',
    label: 'reportsV2Nav',
    icon: ClipboardPen,
    to: '/reports-v2',
    permission: FusawarePermission.ACCESS_ADMIN_PAGE,
  },
  {
    key: 'procedures',
    label: 'procedures',
    icon: ListTodo,
    to: '/procedures',
    permission: FusawarePermission.MANAGE_ALL_PROCEDURE_INSTANCES,
  },
  {
    key: 'reportStudio',
    label: 'reportStudio',
    icon: FileCode,
    to: '/report-studio',
    permission: FusawarePermission.ACCESS_ADMIN_PAGE,
  },
];

const SECTION_PADDING = 'p-[8px] px-[12px]';

export function NavBar() {
  return (
    <SidebarProvider
      className="w-auto"
      style={{ '--sidebar-width-icon': '3.5rem' } as React.CSSProperties}
    >
      <NavBarSidebar />
    </SidebarProvider>
  );
}

function NavBarSidebar() {
  const location = useLocation();
  const userClass = useGlobalStore(state => state.userClass);
  const { isDownloading, download } = useDesktopDownload();

  const isActive = (path: string, matchNested = false) =>
    location.pathname === path || (matchNested && location.pathname.startsWith(`${path}/`));
  const canSee = (item: NavItem) =>
    !item.permission || Boolean(userClass?.fusaHasPermission(item.permission));

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className={SECTION_PADDING}>
        <OrganizationHeader />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className={cn(SECTION_PADDING, 'pt-[40px]')}>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.filter(canSee).map(item => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    isActive={isActive(item.to, item.matchNested)}
                    tooltip={I18n.get(item.label)}
                    render={<Link to={item.to} />}
                  >
                    <item.icon />
                    <span>{I18n.get(item.label)}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className={cn(SECTION_PADDING, 'border-t border-sidebar-border')}>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={isActive('/support-documentation')}
              tooltip={I18n.get('helpAndDocumentation')}
              render={<Link to="/support-documentation" />}
            >
              <Info />
              <span>{I18n.get('helpAndDocumentation')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => void download()}
              disabled={isDownloading}
              tooltip={I18n.get('downloadDesktopApp')}
            >
              <Download />
              <span>{I18n.get('downloadDesktopApp')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <UserMenu />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

function OrganizationHeader() {
  const { open } = useSidebar();
  const enterprise = useGlobalStore(state => state.enterprise);
  const getFileSourceUrl = useGlobalStore(state => state.getFileSourceUrl);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  const fileId = enterprise?.file?.id;

  useEffect(() => {
    if (!fileId) {
      setLogoUrl(undefined);
      return;
    }
    void getFileSourceUrl(fileId).then(response => {
      if (response.status === 200) {
        setLogoUrl(response.downloadUrl);
      }
    });
  }, [fileId, getFileSourceUrl]);

  const title = enterprise?.title ?? I18n.get('fusaware');

  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-8 rounded-md after:hidden">
        <AvatarImage className="rounded-md" src={logoUrl} alt={title} />
        <AvatarFallback className="rounded-md bg-sidebar-accent text-sidebar-accent-foreground text-xs">
          {initialsOf(title)}
        </AvatarFallback>
      </Avatar>
      <span className="flex-1 truncate group-data-[collapsible=icon]:hidden">{title}</span>
      <SidebarTrigger
        aria-label={open ? I18n.get('collapseSidebar') : I18n.get('expandSidebar')}
        className="border-[1px] group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      />
    </div>
  );
}

function UserMenu() {
  const logoutFn = useLogoutFunction();
  const navigate = useNavigate();
  const userClass = useGlobalStore(state => state.userClass);

  return (
    <SidebarUserMenu>
      {/* The admin app is a separate entry point, not a route in this
          router, so this is a plain link that loads it in a new tab. */}
      {userClass?.fusaHasPermission(FusawarePermission.ACCESS_ADMIN_PAGE) && (
        <DropdownMenuItem
          render={<a href="/admin" target="_blank" rel="noopener noreferrer" />}
        >
          {I18n.get('admin')}
          <ExternalLink className="ml-auto" />
        </DropdownMenuItem>
      )}
      {userClass?.fusaHasPermission(FusawarePermission.ACCESS_AI_ASSISTANT) && (
        <>
          <DropdownMenuItem onClick={() => void navigate('/ai-assistant')}>
            {I18n.get('aiAssistant')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void navigate('/ai-approvals')}>
            {I18n.get('aiApprovals')}
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuItem onClick={() => void logoutFn(false)}>
        {I18n.get('logout')}
      </DropdownMenuItem>
    </SidebarUserMenu>
  );
}

function initialsOf(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}
